// src/components/ArticleEditor/RichTextEditor.jsx
import React, { useState } from 'react';
import { Form, Input, Button, Select, message, Card, Space, Tag } from 'antd';
import { SaveOutlined, CloseOutlined, PlusOutlined } from '@ant-design/icons';
import './RichTextEditor.css';

const { TextArea } = Input;
const { Option } = Select;

const RichTextEditor = ({ 
  initialData = {}, 
  onSave, 
  onCancel, 
  mode = 'create',
  loading = false 
}) => {
  const [form] = Form.useForm();
  const [tags, setTags] = useState(initialData.tags || []);
  const [tagInput, setTagInput] = useState('');

  // 表单提交
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      if (!values.content || !values.content.trim()) {
        message.error('请填写文章内容');
        return;
      }

      const articleData = {
        ...values,
        tags,
        wordCount: values.content.trim().split(/\s+/).length,
        readTime: Math.ceil(values.content.trim().length / 500)
      };

      await onSave(articleData);
    } catch (error) {
      console.error('表单验证失败:', error);
    }
  };

  // 处理标签
  const handleTagInputConfirm = () => {
    if (tagInput && !tags.includes(tagInput)) {
      setTags([...tags, tagInput]);
    }
    setTagInput('');
  };

  const removeTag = (removedTag) => {
    const newTags = tags.filter(tag => tag !== removedTag);
    setTags(newTags);
  };

  // 常用标签
  const commonTags = [
    '技术', '前端', 'React', 'JavaScript', 'CSS', 
    'Node.js', 'TypeScript', 'Vue', 'Web开发', '编程'
  ];

  return (
    <div className="rich-text-editor">
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          title: initialData.title || '',
          excerpt: initialData.excerpt || '',
          content: initialData.content || '',
          category: initialData.category || '技术',
          author: initialData.author || ''
        }}
      >
        {/* 基本信息 */}
        <Card title="基本信息" size="small" className="editor-section">
          <Form.Item
            name="title"
            label="文章标题"
            rules={[
              { required: true, message: '请输入文章标题' },
              { max: 100, message: '标题不能超过100个字符' }
            ]}
          >
            <Input 
              placeholder="请输入文章标题" 
              size="large"
              maxLength={100}
              showCount
            />
          </Form.Item>

          <Form.Item
            name="excerpt"
            label="文章摘要"
          >
            <TextArea
              placeholder="请输入文章摘要（可选）"
              rows={3}
              maxLength={300}
              showCount
            />
          </Form.Item>

          <div className="form-row">
            <Form.Item
              name="category"
              label="分类"
              style={{ flex: 1 }}
            >
              <Select placeholder="选择分类">
                <Option value="技术">技术</Option>
                <Option value="前端">前端</Option>
                <Option value="后端">后端</Option>
                <Option value="移动开发">移动开发</Option>
                <Option value="生活">生活</Option>
                <Option value="读书">读书</Option>
                <Option value="其他">其他</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="author"
              label="作者"
              style={{ flex: 1 }}
            >
              <Input 
                placeholder="作者名称" 
              />
            </Form.Item>
          </div>
        </Card>

        {/* 标签 */}
        <Card title="文章标签" size="small" className="editor-section">
          <div className="tags-container">
            <div className="current-tags">
              {tags.map(tag => (
                <Tag 
                  key={tag} 
                  closable 
                  onClose={() => removeTag(tag)}
                  className="article-tag"
                >
                  {tag}
                </Tag>
              ))}
            </div>
            
            <div className="tag-input-area">
              <Input
                size="small"
                value={tagInput}
                onChange={e => setTagInput(e.target.value)}
                onBlur={handleTagInputConfirm}
                onPressEnter={handleTagInputConfirm}
                placeholder="输入标签后按回车"
                style={{ width: 200 }}
              />
              <span className="tag-input-tip">按回车或点击外部添加标签</span>
            </div>

            <div className="common-tags">
              <span className="common-tags-label">常用标签：</span>
              {commonTags.map(tag => (
                <Tag 
                  key={tag}
                  className="common-tag"
                  onClick={() => {
                    if (!tags.includes(tag)) {
                      setTags([...tags, tag]);
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  {tag}
                </Tag>
              ))}
            </div>
          </div>
        </Card>

        {/* 内容编辑器 */}
        <Card title="文章内容" size="small" className="editor-section content-section">
          <Form.Item
            name="content"
            label=""
            rules={[
              { required: true, message: '请输入文章内容' },
              { min: 50, message: '内容太短，请至少输入50个字' }
            ]}
          >
            <TextArea
              placeholder="开始你的创作..."
              rows={20}
              style={{ fontFamily: 'monospace' }}
            />
          </Form.Item>
          
          <div className="editor-tips">
            <p>💡 支持Markdown格式</p>
            <p>📝 建议字数：800-2000字</p>
          </div>
        </Card>
      </Form>

      {/* 操作按钮 */}
      <div className="editor-actions">
        <Space>
          <Button 
            onClick={onCancel}
            icon={<CloseOutlined />}
          >
            取消
          </Button>
          <Button 
            type="primary" 
            onClick={handleSubmit}
            icon={<SaveOutlined />}
            loading={loading}
            size="large"
          >
            {mode === 'create' ? '发布文章' : '保存修改'}
          </Button>
        </Space>
      </div>
    </div>
  );
};

export default RichTextEditor;